import { config } from '../config';

const API_URL = config.API_BASE_URL;

// Types
export interface AuthUser {
  id: string;
  email: string;
  created_at: string;
  is_active: boolean;
}

export interface AuthSession {
  access_token: string;
  token_type: string;
  user: AuthUser;
}

// Store token in localStorage
const TOKEN_KEY = 'admin_auth_token';

const setToken = (token: string) => {
  localStorage.setItem(TOKEN_KEY, token);
};

const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY);
};

// Login with email and password
export const signIn = async (email: string, password: string): Promise<AuthSession> => {
  console.log('signIn called with email:', email);
  console.log('API_URL:', API_URL);
  const url = `${API_URL}/api/auth/token`;
  console.log('Full login URL:', url);
  
  const body = new URLSearchParams({
    username: email,
    password: password,
  });
  console.log('Request body:', body.toString());
  
  try {
    console.log('Making login request...');
    
    // Create an AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.error('Login request timed out after 10 seconds');
      controller.abort();
    }, 10000); // 10 second timeout
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body,
      signal: controller.signal,
    }).finally(() => {
      clearTimeout(timeoutId);
    });
    
    console.log('Login response status:', response.status);

    if (!response.ok) {
      const error = await response.json();
      console.error('Login error response:', error);
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();
    console.log('Login success, data:', data);
    
    // Store the token
    setToken(data.access_token);
    
    return {
      access_token: data.access_token,
      token_type: data.token_type,
      user: data.user,
    };
  } catch (error) {
    console.error('Login request failed:', error);
    throw error;
  }
};

// Sign out
export const signOut = async (): Promise<void> => {
  const token = getToken();
  
  if (token) {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  }
  
  // Always remove token locally
  removeToken();
};

// Get current user
export const getCurrentUser = async (): Promise<AuthUser | null> => {
  const token = getToken();
  
  if (!token) {
    return null;
  }

  try {
    const response = await fetch(`${API_URL}/api/auth/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      // Token is invalid
      removeToken();
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Get current user error:', error);
    removeToken();
    return null;
  }
};

// Check if user is authenticated
export const isAuthenticated = async (): Promise<boolean> => {
  const user = await getCurrentUser();
  return user !== null;
};

// Get auth token for API requests
export const getAuthToken = async (): Promise<string | null> => {
  return getToken();
};
